<!-- Backdrop — click closes the panel. Always rendered when open so
     the fade-in animation lands cleanly. -->
@if (open()) {
  <div class="wn-backdrop"
       (click)="shellState.closeWhatsNew()"
       aria-hidden="true"></div>
}

<aside class="wn-panel"
       [class.is-open]="open()"
       role="dialog"
       aria-modal="true"
       [attr.aria-label]="'whats-new.whats_new_panel.what_s_new_release_notes' | transloco"
       [attr.aria-hidden]="open() ? null : 'true'"
       [attr.inert]="open() ? null : ''">

  <!-- ─── Header ─────────────────────────────────────── -->
  <header class="wn-panel__header">
    <div class="wn-panel__title-block">
      <h2 class="wn-panel__title">
        <paper-icon name="sparkles" [size]="18" />
        {{ 'whats-new.whats_new_panel.what_s_new' | transloco }}
      </h2>
      <p class="wn-panel__subtitle">
        {{ 'whats-new.whats_new_panel.everything_we_ve_shipped_newest_first' | transloco }}
      </p>
    </div>
    <paper-button
      intent="ghost"
      size="sm"
      shape="icon"
      icon="x"
      [ariaLabel]="'whats_new.close_panel' | transloco"
      (click)="shellState.closeWhatsNew()" />
  </header>

  <!-- ─── Body ───────────────────────────────────────── -->
  <div class="wn-panel__body">
    @if (entries().length === 0) {
      <p class="wn-panel__empty">{{ 'whats-new.whats_new_panel.no_release_notes_yet_check_back' | transloco }}</p>
    } @else {
      <ol class="wn-feed" role="list">
        @for (entry of entries(); track entry.id) {
          <li
            class="wn-entry"
            [class.wn-entry--unread]="isUnread(entry)"
            [attr.data-release-id]="entry.id"
            [attr.aria-label]="entry.title">

            <!-- Per-entry header — date + version + tags + 'New' badge. -->
            <div class="wn-entry__meta">
              <span class="wn-entry__date">{{ formatDate(entry.date) }}</span>
              @if (entry.version) {
                <span class="wn-entry__version">v{{ entry.version }}</span>
              }
              @if (entry.tags && entry.tags.length > 0) {
                <span class="wn-entry__tag-row">
                  @for (tag of entry.tags; track tag) {
                    <span class="wn-entry__tag">{{ tag }}</span>
                  }
                </span>
              }
              @if (isUnread(entry)) {
                <span class="wn-entry__new-badge" [attr.aria-label]="'whats-new.whats_new_panel.new' | transloco">
                  <span class="wn-entry__new-dot"></span>
                  {{ 'whats-new.whats_new_panel.new_2' | transloco }}
                </span>
              }
            </div>

            <h3 class="wn-entry__title">{{ entry.title }}</h3>
            <p class="wn-entry__summary">{{ entry.summary }}</p>

            <!-- Highlights — same icon + title + text rhythm as the
                 onboarding Flow step. -->
            @if (entry.highlights.length > 0) {
              <ul class="wn-highlights" role="list">
                @for (h of entry.highlights; track h.title) {
                  <li class="wn-highlight">
                    @if (h.icon) {
                      <span class="wn-highlight__icon" aria-hidden="true">
                        <paper-icon [name]="h.icon" [size]="16" />
                      </span>
                    }
                    <span class="wn-highlight__text">
                      <span class="wn-highlight__title">{{ h.title }}</span>
                      <span class="wn-highlight__desc">{{ h.text }}</span>
                    </span>
                  </li>
                }
              </ul>
            }

            <!-- Optional media — schema-supported from day one;
                 internal-beta entries leave this absent. -->
            @if (entry.media && entry.media.length > 0) {
              <div class="wn-media">
                @for (m of entry.media; track trackMedia($index, m)) {
                  @switch (m.kind) {
                    @case ('image') {
                      <figure class="wn-media__item wn-media__item--image">
                        <img [src]="m.src"
                             [alt]="m.alt || ''"
                             loading="lazy" />
                        @if (m.caption) {
                          <figcaption class="wn-media__caption">{{ m.caption }}</figcaption>
                        }
                      </figure>
                    }
                    @case ('youtube') {
                      <figure class="wn-media__item wn-media__item--video">
                        <div class="wn-media__frame">
                          <iframe
                            [src]="youtubeEmbed(m.src)"
                            [title]="m.alt || entry.title"
                            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowfullscreen
                            loading="lazy"
                            referrerpolicy="strict-origin-when-cross-origin"></iframe>
                        </div>
                        @if (m.caption) {
                          <figcaption class="wn-media__caption">{{ m.caption }}</figcaption>
                        }
                      </figure>
                    }
                    @case ('video') {
                      <figure class="wn-media__item wn-media__item--video">
                        <div class="wn-media__frame">
                          <video [src]="m.src"
                                 controls
                                 preload="metadata"
                                 [attr.aria-label]="m.alt || entry.title"></video>
                        </div>
                        @if (m.caption) {
                          <figcaption class="wn-media__caption">{{ m.caption }}</figcaption>
                        }
                      </figure>
                    }
                  }
                }
              </div>
            }

            <!-- Optional links — small chip row at the foot. -->
            @if (entry.links && entry.links.length > 0) {
              <ul class="wn-links" role="list">
                @for (link of entry.links; track link.url) {
                  <li>
                    <a class="wn-link"
                       [href]="link.url"
                       target="_blank"
                       rel="noopener noreferrer"
                       (click)="onLinkClick(entry, link)">
                      <paper-icon
                        [name]="link.icon || 'external-link'"
                        [size]="11"
                        aria-hidden="true" />
                      {{ link.label }}
                    </a>
                  </li>
                }
              </ul>
            }
          </li>
        }
      </ol>

      <p class="wn-panel__caught-up">
        <paper-icon name="check" [size]="12" aria-hidden="true" />
        {{ 'whats-new.whats_new_panel.you_re_all_caught_up' | transloco }}
      </p>
    }
  </div>
</aside>
